import { useState, useEffect, useCallback } from "react";
import { trpc } from "../lib/trpc.js";

interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isSuperAdmin: boolean;
  organizationId: string;
  organizationName: string;
  organizationPlan: string;
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem("accessToken"));

  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Listen for auth:logout events dispatched by the trpc client when refresh fails
  useEffect(() => {
    function handleForceLogout() {
      setIsAuthenticated(false);
    }
    window.addEventListener("auth:logout", handleForceLogout);
    return () => window.removeEventListener("auth:logout", handleForceLogout);
  }, []);

  // If meQuery returns 401 error, clear auth state
  useEffect(() => {
    if (meQuery.error && meQuery.error.data?.code === "UNAUTHORIZED") {
      // Only logout if we don't have a valid token anymore
      const token = localStorage.getItem("accessToken");
      if (!token) {
        setIsAuthenticated(false);
      }
    }
  }, [meQuery.error]);

  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: (data) => {
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);
      setIsAuthenticated(true);
    },
  });

  const registerMutation = trpc.auth.register.useMutation({
    onSuccess: (data) => {
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);
      setIsAuthenticated(true);
    },
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      setIsAuthenticated(false);
    },
    onError: () => {
      // Even if logout fails on server, clear local state
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      setIsAuthenticated(false);
    },
  });

  const logout = useCallback(() => {
    // Try server logout, but always clear local state
    try {
      logoutMutation.mutate();
    } catch {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      setIsAuthenticated(false);
    }
  }, [logoutMutation]);

  const user: AuthUser | null = meQuery.data ? {
    id: meQuery.data.id,
    email: meQuery.data.email,
    name: meQuery.data.name,
    role: meQuery.data.role,
    isSuperAdmin: (meQuery.data as any).isSuperAdmin ?? false,
    organizationId: meQuery.data.organizationId,
    organizationName: meQuery.data.organizationName,
    organizationPlan: meQuery.data.organizationPlan,
  } : null;

  return {
    user,
    isAuthenticated,
    isLoading: isAuthenticated && meQuery.isLoading,
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    logout,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  };
}
